import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Logo } from '../components/Logo'
import { useTenant } from '../context/TenantContext'
import { useOnboardingStore } from '../store/onboardingStore'
import { buildSubmissionPayload } from '../utils/submissionPayload'
import { useTestMode } from '../context/TestModeContext'
import { submissionService } from '../services/api'
import { logger } from '../utils/logger'
import { Globe, Instagram } from 'lucide-react'
import { useIsItaly } from './italy/useIsItaly'
import { completePtCase } from '../flow/ptComplete'

// TikTok icon component since lucide-react doesn't have one
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  )
}

const PLATFORM_ICONS: Record<string, { icon: React.ReactNode; bgClass: string }> = {
  website: { icon: <Globe className="w-5 h-5 text-primary" />, bgClass: 'bg-primary/10' },
  instagram: { icon: <Instagram className="w-5 h-5 text-pink-600" />, bgClass: 'bg-pink-100' },
  tiktok: { icon: <TikTokIcon className="w-5 h-5 text-white" />, bgClass: 'bg-gray-900' },
}


// Default social links for backward compat (Germany)
const DEFAULT_SOCIAL_LINKS = [
  { platform: 'website', handle: 'gam-medical.de', url: 'https://gam-medical.de' },
  { platform: 'instagram', handle: '@gam.adhs', url: 'https://www.instagram.com/gam.adhs/' },
  { platform: 'tiktok', handle: '@gam.adhs', url: 'https://www.tiktok.com/@gam.adhs' },
]

export function Step12Complete() {
  const { t } = useTranslation()
  const { tenant } = useTenant()
  const isItaly = useIsItaly()
  const isTestMode = useTestMode()
  const submitted = useRef(false)
  const {
    patientNumber,
    personalData,
    cohort,
  } = useOnboardingStore()
  const returningPt = cohort === 'psychotherapy_returning'

  const socialLinks = (tenant?.socialLinks as typeof DEFAULT_SOCIAL_LINKS | null) || DEFAULT_SOCIAL_LINKS
  const contactEmail = tenant?.contactEmail || 'info@gam-medical.de'

  // Submit data if SEPA step was skipped (submission normally happens in Step11)
  useEffect(() => {
    if (submitted.current) return
    submitted.current = true

    const submitIfNeeded = async () => {
      // Psychotherapy case not completed yet (returning patients with a mandate have no payment
      // step): do it now. Test links complete too — their case is marked legallyBinding:false.
      const st = useOnboardingStore.getState()
      if (st.cohort !== 'standard' && !st.ptCompleted) {
        const method = st.ptReturning?.hasMandate || !st.sepaOptOut ? 'sepa' : 'rechnung'
        const result = await completePtCase(method)
        if (result !== 'ok') logger.error('[Complete] psychotherapy case not completed:', result)
      }
      // Returning psychotherapy patients completed their onboarding before — no second submission
      if (st.cohort === 'psychotherapy_returning') return
      // Test mode must never create a real submission / webhook / PsyCloud sync
      if (isTestMode) {
        logger.log('[Complete] Test mode — skipping submission')
        return
      }
      // Skip if already submitted (e.g. via Step11 SEPA) or no data
      if (sessionStorage.getItem('submission-sent') === patientNumber) return
      if (!patientNumber || !personalData.firstName) return

      try {
        const result = await submissionService.create({

          ...buildSubmissionPayload(useOnboardingStore.getState(), { sepaOptOut: true }),

          webhookSent: false,

        })
        if (result.success) {
          sessionStorage.setItem('submission-sent', patientNumber)
          logger.log('[Complete] Submission stored:', result.data?.id, '- webhook:', result.data?.webhookSent ? 'sent' : 'not sent')
        }
      } catch (err) {
        logger.error('[Complete] Failed to store submission:', err)
      }
    }

    submitIfNeeded()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="form-container">
      <div className="form-inner py-12">
        <div className="flex justify-center mb-8">
          {/* Italy asked for a larger logo on the final screen */}
          <Logo size={isItaly ? 220 : 150} />
        </div>

        <h1 className="text-h1 text-foreground mb-4 text-center">
          {t('step12.title')}
        </h1>

        <p className="text-body text-muted-foreground mb-8 max-w-md mx-auto text-center">
          {t('step12.subtitle')}
          {returningPt ? (
            <>
              <br /><br />
              {t('pt.complete.returning')}
            </>
          ) : (tenant?.contracts?.length ?? 0) > 0 && (
            <>
              <br /><br />
              {t('step12.documentsByEmail', { email: personalData.email || '' })}
            </>
          )}
        </p>

        {socialLinks.length > 0 && (
          <div className="bg-white rounded-card p-6 mb-8 max-w-md mx-auto">
            <h3 className="text-h3 text-foreground mb-4 text-center">{t('step12.followUs')}</h3>

            <div className="space-y-4">
              {socialLinks.map((link) => {
                const platformKey = link.platform.toLowerCase()
                const config = PLATFORM_ICONS[platformKey] || PLATFORM_ICONS.website
                const label = platformKey === 'website' ? t('step12.website') : link.platform.charAt(0).toUpperCase() + link.platform.slice(1)

                return (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`w-10 h-10 ${config.bgClass} rounded-full flex items-center justify-center`}>
                      {config.icon}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{label}</p>
                      <p className="text-small text-muted-foreground">{link.handle}</p>
                    </div>
                  </a>
                )
              })}
            </div>
          </div>
        )}

        <p className="text-small text-muted-foreground text-center">
          {t('step12.contactInfo')}<br />
          <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">
            {contactEmail}
          </a>
        </p>
      </div>
    </div>
  )
}
